/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
import ga from '../../helpers/ga';
import { lvtMetricEvent } from '..';
import { LVTMetricData } from '../types';
import { hashString } from '../../helpers/hash';
import { user } from '../../api';
import {
  LVT_METRIC_CHECKOUT_ANALYSIS_STEPS,
  LVT_METRIC_CHECKOUT_ANALYSIS_STORAGE_KEYS,
} from './constants';
import { LVTMetricCheckoutAnalysisStep } from './types';

export const getStorageNameEntry = (stepName: string): string =>
  `${stepName}_entry`;
export const getStorageNameExit = (stepName: string): string =>
  `${stepName}_exit`;

export const cleanupStorages = (): void => {
  Object.values(LVT_METRIC_CHECKOUT_ANALYSIS_STORAGE_KEYS).forEach((key) => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });
  Object.values(LVT_METRIC_CHECKOUT_ANALYSIS_STEPS).forEach((step) => {
    sessionStorage.removeItem(step);
    sessionStorage.removeItem(getStorageNameEntry(step));
    sessionStorage.removeItem(getStorageNameExit(step));
  });
};

export const addCheckoutStepEvent = (
  stepName: NonNullable<LVTMetricData<'checkout_analysis'>['eventStep']>,
  data: {
    sessionId: string;
    timestampEntry?: number;
    timestampExit?: number;
    timeSpent?: number;
    basketId?: string;
  },
  storageStepName?: string
): void => {
  // TODO: remove 'if' after metrics dark launch strategies would be over
  if (window.location.origin !== 'https://stage.rbo.org.uk') return;
  const [event, enhancedData] = lvtMetricEvent(
    'checkout_analysis',
    data,
    'step',
    stepName,
    'learning-platform'
  );
  ga.sendEvent(event, enhancedData);
  if (storageStepName) {
    sessionStorage.setItem(
      storageStepName,
      JSON.stringify({ event, data: enhancedData })
    );
  }
};

export const exitPrevEvent = (
  sessionId: string,
  stepName: NonNullable<LVTMetricData<'checkout_analysis'>['eventStep']>,
  saveToStorage?: boolean
): void => {
  // TODO: remove 'if' after metrics dark launch strategies would be over
  if (window.location.origin !== 'https://stage.rbo.org.uk') return;
  const timestampExit = Date.now();
  const prevTimestamp = Number(sessionStorage.getItem(stepName));
  const basketId =
    sessionStorage.getItem(
      LVT_METRIC_CHECKOUT_ANALYSIS_STORAGE_KEYS.basketId
    ) || undefined;
  const data = {
    sessionId,
    timestampExit,
    timeSpent: prevTimestamp ? timestampExit - prevTimestamp : undefined,
    basketId,
  };
  addCheckoutStepEvent(
    stepName,
    data,
    saveToStorage ? getStorageNameExit(stepName) : undefined
  );
};

export const entryNextEvent = (
  sessionId: string,
  stepName: NonNullable<LVTMetricData<'checkout_analysis'>['eventStep']>,
  saveToStorage?: boolean,
  additionalData = {}
): void => {
  // TODO: remove 'if' after metrics dark launch strategies would be over
  if (window.location.origin !== 'https://stage.rbo.org.uk') return;
  const timestampEntry = Date.now();
  const basketId =
    sessionStorage.getItem(
      LVT_METRIC_CHECKOUT_ANALYSIS_STORAGE_KEYS.basketId
    ) || undefined;
  const data = {
    sessionId,
    timestampEntry,
    basketId,
    ...additionalData,
  };
  localStorage.setItem(
    LVT_METRIC_CHECKOUT_ANALYSIS_STORAGE_KEYS.stepName,
    stepName
  );
  sessionStorage.setItem(stepName, String(timestampEntry));
  addCheckoutStepEvent(
    stepName,
    data,
    saveToStorage ? getStorageNameEntry(stepName) : undefined
  );
};

export const addAbandonedCheckoutStepEvent = (data: {
  sessionId: string;
  timestamp: number;
  abandonedCheckout: boolean;
  lastStepReached?: string;
}): void => {
  // TODO: remove 'if' after metrics dark launch strategies would be over
  if (window.location.origin !== 'https://stage.rbo.org.uk') return;
  ga.sendEvent(
    ...lvtMetricEvent(
      'checkout_analysis',
      data,
      'abandoned',
      'checkout_abandoned',
      'learning-platform'
    )
  );
};

export const abandoned = (): void => {
  // TODO: remove 'if' after metrics dark launch strategies would be over
  if (window.location.origin !== 'https://stage.rbo.org.uk') return;
  const sessionId = localStorage.getItem(
    LVT_METRIC_CHECKOUT_ANALYSIS_STORAGE_KEYS.sessionId
  );
  if (!sessionId) {
    console.warn('Metrics: sessionId was not found');
    return;
  }
  const lastStepReached =
    localStorage.getItem(LVT_METRIC_CHECKOUT_ANALYSIS_STORAGE_KEYS.stepName) ||
    undefined;
  const data = {
    sessionId,
    lastStepReached,
    timestamp: Date.now(),
    abandonedCheckout: true,
  };
  addAbandonedCheckoutStepEvent(data);
  cleanupStorages();
};

// Due to switching parts of the app sometimes events from dataLayer could be lost.
// That's why they are needed to be repushed on next page load
const addCheckoutAnalysisEventListener = (
  stepName: NonNullable<LVTMetricData<'checkout_analysis'>['eventStep']>,
  storageNameGetter: (key: string) => string
): void => {
  if (ga.checkLVTMetricEventExistence(stepName)) return;
  const gaEventString = sessionStorage.getItem(storageNameGetter(stepName));
  if (!gaEventString) {
    console.warn('gaEventString was not found in session storage, skipping');
    return;
  }
  try {
    const gaEvent = JSON.parse(gaEventString);
    addCheckoutStepEvent(stepName, gaEvent.data);
  } catch (err) {
    console.error('GA Event (session storage) parsing failed');
  }
};

export const trackCheckoutAnalysisEvents = (): void => {
  // TODO: remove 'if' after metrics dark launch strategies would be over
  if (window.location.origin !== 'https://stage.rbo.org.uk') return;
  window.addEventListener('beforeunload', () => {
    localStorage.setItem(
      LVT_METRIC_CHECKOUT_ANALYSIS_STORAGE_KEYS.closedWebsiteAt,
      Date.now().toString()
    );
  });
  window.addEventListener('load', () => {
    const startCheckoutTime = Number(
      localStorage.getItem(
        LVT_METRIC_CHECKOUT_ANALYSIS_STORAGE_KEYS.startCheckoutTime
      )
    );
    const closedWebsiteAt = Number(
      localStorage.getItem(
        LVT_METRIC_CHECKOUT_ANALYSIS_STORAGE_KEYS.closedWebsiteAt
      ) ?? 0
    );
    const now = Date.now();
    const hour = 3600000; // 1 hour

    if (closedWebsiteAt - startCheckoutTime >= hour) {
      abandoned();
      return;
    }

    if (now - startCheckoutTime >= hour) {
      abandoned();
    }
  });
  window.addEventListener('load', () =>
    addCheckoutAnalysisEventListener(
      LVT_METRIC_CHECKOUT_ANALYSIS_STEPS.SCHOOLS_CPD_EVENTS_ENTRY,
      getStorageNameExit
    )
  );
  window.addEventListener('load', () =>
    addCheckoutAnalysisEventListener(
      LVT_METRIC_CHECKOUT_ANALYSIS_STEPS.SCHOOLS_CPD_EVENTS_ADD_TO_BASKET,
      getStorageNameEntry
    )
  );
  window.addEventListener('load', () =>
    addCheckoutAnalysisEventListener(
      LVT_METRIC_CHECKOUT_ANALYSIS_STEPS.SCHOOLS_CPD_EVENTS_ADD_TO_BASKET,
      getStorageNameExit
    )
  );
};

export const startTrackingCheckoutAnalysisEvents = async (options: {
  from: LVTMetricCheckoutAnalysisStep;
}): Promise<void> => {
  const {
    data: {
      attributes: { sessionKey: webSessionKey },
    },
  } = await user.get({});
  const sessionId = await hashString(webSessionKey);
  const timestampEntry = Date.now();

  localStorage.setItem(
    LVT_METRIC_CHECKOUT_ANALYSIS_STORAGE_KEYS.sessionId,
    sessionId
  );
  sessionStorage.setItem(
    LVT_METRIC_CHECKOUT_ANALYSIS_STORAGE_KEYS.entryStepName,
    options.from
  );
  sessionStorage.setItem(options.from, timestampEntry.toString());
  localStorage.setItem(
    LVT_METRIC_CHECKOUT_ANALYSIS_STORAGE_KEYS.stepName,
    options.from
  );
  localStorage.setItem(
    LVT_METRIC_CHECKOUT_ANALYSIS_STORAGE_KEYS.startCheckoutTime,
    timestampEntry.toString()
  );
  localStorage.removeItem(
    LVT_METRIC_CHECKOUT_ANALYSIS_STORAGE_KEYS.closedWebsiteAt
  );
  addCheckoutStepEvent(
    options.from,
    { sessionId, timestampEntry },
    getStorageNameEntry(options.from)
  );
};

export const stepTransition = (options: {
  to: NonNullable<LVTMetricData<'checkout_analysis'>['eventStep']>;

  /** Defaults to the previous step in `localStorage.stepName` */
  from?: NonNullable<LVTMetricData<'checkout_analysis'>['eventStep']>;

  /** Defaults to `true` */
  saveToStorage?: boolean;
}): void => {
  const saveToStorage = options.saveToStorage ?? true;
  const sessionIdKey = LVT_METRIC_CHECKOUT_ANALYSIS_STORAGE_KEYS.sessionId;
  const sessionId =
    sessionStorage.getItem(sessionIdKey) || localStorage.getItem(sessionIdKey);
  if (!sessionId) {
    console.warn('Metrics: sessionId was not found');
    return;
  }

  const from =
    options.from ??
    (localStorage.getItem(
      LVT_METRIC_CHECKOUT_ANALYSIS_STORAGE_KEYS.stepName
    ) as LVTMetricCheckoutAnalysisStep | undefined);

  if (!from) {
    console.warn('Metrics: stepName was not found');
    return;
  }

  exitPrevEvent(sessionId, from, saveToStorage);
  entryNextEvent(sessionId, options.to, saveToStorage);
};

export const withStepTransition = <T extends (...args: any[]) => any>(
  cb: T,
  options: Parameters<typeof stepTransition>[0]
): T =>
  ((...args) => {
    stepTransition(options);
    return cb(...args);
  }) as T;
